// React-Screen CMainPOS (Strangler, Runbook: tools/REACT-STRANGLER.md).
//
// Der Hauptbildschirm - der letzte grosse Brocken und in einer eigenen Liga:
// 2020 Elemente, 730 CPushButtons, 743 CCaptions, 15 eigene Kinder. Die
// Bestandsaufnahme, die Namens-Inventur und die geklaerten Risiken stehen im
// Runbook; hier nur, was beim LESEN dieser Datei noetig ist.
//
// DAS GERUEST-WERKZEUG WURDE BEWUSST NICHT BENUTZT. Es transkribiert das DOM
// woertlich - richtig fuer die 28 statischen Screens davor, falsch fuer
// diesen: die 730 Produkttasten kommen zur Laufzeit aus der Datenbank
// (makeProdCatButtons/makeProdButtons), und dataimport.js baut sie nach jedem
// Import neu auf. Eingefroren in JSX waeren sie beim ersten Sortimentswechsel
// falsch. Deshalb von Hand gegen die Widget-Bibliothek geschrieben, mit
// knoepfeAusLegacy() als Bruecke zu den unveraenderten make*Buttons-Methoden.
//
// DIE KINDER SIND EIN STABILES PRAEFIX (0..14) - siehe Runbook. CMainPOS
// haengt neun weitere Komponenten per addChild() in DIESELBE React-Wurzel
// (CPriceInputMode, CTransactionShowMode, sechs secondaryPanel). React laesst
// nachgestellte Fremdknoten in Ruhe, solange es nie am ENDE seiner eigenen
// Liste einfuegen muss. Sichtbarkeit deshalb IMMER ueber style.visibility -
// so macht es das Legacy ohnehin - und NIEMALS ueber bedingtes Rendern.
import { memo } from 'react';
import { defineReactScreen } from '../bridge.js';
import {
  PushPanel, NumPad, TabbedPushPanel, Frame, Image, InputField, TextLabel,
  DataGrid, Pre, knoepfeAusLegacy, gitterVorbereiten, preVorbereiten,
  captionVorbereiten, SchreibCaption, verdrahteTextLabel,
  verdrahteInputField, verdrahteTabbedPushPanel, reDrawUeberReact,
} from '../widgets.jsx';
import { merken as kiMerken, vergessen as kiVergessen } from '../../pos/kiRueckmeldung.js';

// --- Stile: aus der FIXTURE, nicht aus der Layout-XML ---------------------
// Teuer gelernt am 24.8.2026. Die XML ist NICHT das Soll:
//
// 1. KLEBRIGE STRING-STILE. leftPad, numPad, prodSelect, sumcaption, suminfo
//    und infoIcons haben in der Fixture style="" - also GAR KEINE Inline-
//    Stile, obwohl die Screen-XML welche angibt. Grund: ein Whitespace-
//    <styles> in der Vorgabe-XML macht properties.styles zu einem STRING,
//    und combine() kann einen String-Default nicht mehr durch ein Objekt
//    ersetzen (fuer den NumPad steht das seit ESM-Welle 5a in mainpos.js:107
//    dokumentiert - es betrifft aber sechs der 15 Kinder). Ihr Layout kommt
//    allein aus den CSS-Klassen.
// 2. LAUFZEIT-UEBERSCHREIBUNGEN. payment, payment2, bonedit und bonfnc stehen
//    in der XML auf hidden, prepare() setzt sie auf visible. Ebenso das
//    Reiter-Panel: sein background-color #AEBBDF steht ueberhaupt nur in
//    mainpos.js:62.
// 3. REIHENFOLGE. Der Stil-String wird ZEICHENGENAU verglichen (canonicalDom
//    sortiert nur Attribute, nicht die Eigenschaften darin). Die Reihenfolge
//    entsteht aus combine() plus Laufzeit-Zuweisungen und ist aus der XML
//    nicht ableitbar - beim Inhalts-Panel steht in der XML
//    visibility,right,top,height,width, in der Fixture aber
//    height,width,top,visibility,right.
//
// Werkzeug zum Nachschlagen: tools/fixture-teil.py CMainPOS <pfad>
//
// Die Icon-Stile bleiben Konstanten (RISIKO 1 des Runbooks): pos/infoicons.js
// schreibt properties.styles.backgroundImage OHNE reStyle() - im Legacy wirkt
// das nicht. Aus dem Handle gelesen wuerde es ploetzlich wirken.
const S = {
  topPad: { visibility: 'hidden', height: '3%', top: '0%', left: '1%', width: '100%' },
  topPadKnopf: { border: '2px solid #94C494', width: '6vw', height: '5vh' },
  // Reiter- und Inhalts-Panel der Produktauswahl (Pfade 3:0 und 3:1).
  reiter: { height: '22.5%', width: '30%', left: '70%', backgroundColor: '#AEBBDF' },
  inhalte: { height: '75.0%', width: '30%', top: '22.5%', visibility: 'visible', right: '0px' },
  payment: { visibility: 'visible', height: '22%', bottom: '17.5%', left: '55%',
             width: '15.5%', zIndex: '-1' },
  paymentKnopf: { width: '93.5%' },
  payment2: { visibility: 'visible', height: '15%', bottom: '2.5%', left: '55%',
              width: '15.5%', zIndex: '-1' },
  payment2Knopf: { width: '43.3%' },
  bonedit: { visibility: 'visible', height: '60%', top: '0px', left: '55%',
             width: '15.5%', zIndex: '-1' },
  boneditKnopf: { width: '93.5%' },
  bonfnc: { visibility: 'visible', height: '30%', width: '15.23%', bottom: '2.5%',
            left: '15.23%' },
  bonContainer: { position: 'absolute', visibility: 'visible' },
  gitter: { fontSize: '2.0vh' },
  bonSum: { borderTopStyle: 'double', borderTopWidth: '3px', fontSize: '1.3vw',
            margin: '0px', padding: '2px' },
  info: { visibility: 'visible' },
  statusBar: { position: 'absolute', left: '0px', bottom: '0px', width: '100%',
               height: '2.13%', borderStyle: 'solid', borderWidth: '1px' },
  userInfo: { position: 'absolute', top: '2px', left: '4px', width: '180px',
              fontSize: '11px', textAlign: 'left', fontWeight: 'normal' },
  versionInfo: { position: 'absolute', top: '2px', right: '16.60%', width: '14.65%',
                 fontSize: '11px', textAlign: 'right', fontWeight: 'normal' },
  dateTimeInfo: { position: 'absolute', top: '2px', right: '4px', width: '12.7%',
                  fontSize: '11px', textAlign: 'right', paddingLeft: '4px',
                  borderLeftWidth: '1px', borderLeftStyle: 'solid' },
  infoIconKnopf: { width: '5vw', height: '5vh', backgroundSize: '70% 100%', border: '0px' },
  posysseus: { position: 'absolute', visibility: 'hidden', width: '33.89%',
               height: '44.63%', right: '24.41%', top: '18.75%' },
};

function MainPOSScreen({ bridge }) {
  return (
    <>
      {/* 0 topPad */}
      <PushPanel bridge={bridge} pfad="0" zeilen={1} spalten={5}
                 knoepfe={bridge.topPadKnoepfe || []} style={S.topPad}
                 knopfStyle={S.topPadKnopf} />
      {/* 1 leftPad */}
      <PushPanel bridge={bridge} pfad="1" zeilen={13} spalten={1}
                 knoepfe={bridge.leftPadKnoepfe || []} addclass="leftpushpanel" />
      {/* 2 numPad */}
      {/* KEIN style: die Fixture zeigt style="" - CNumPad.properties.styles
          ist ein klebriger String, die XML-Stile werden still verworfen. */}
      <NumPad bridge={bridge} pfad="2" typ="phoneAmount" />
      {/* 3 prodSelect - eigene Komponente, damit React.memo greift (Risiko 2) */}
      <ProdAuswahl bridge={bridge} pfad="3" stand={bridge.state.prodStand || 0} />
      {/* 4 payment */}
      <PushPanel bridge={bridge} pfad="4" zeilen={3} spalten={1}
                 knoepfe={bridge.paymentKnoepfe || []} style={S.payment}
                 knopfStyle={S.paymentKnopf} />
      {/* 5 payment2 */}
      <PushPanel bridge={bridge} pfad="5" zeilen={2} spalten={2}
                 knoepfe={bridge.payment2Knoepfe || []} style={S.payment2}
                 knopfStyle={S.payment2Knopf} />
      {/* 6 bonedit */}
      <PushPanel bridge={bridge} pfad="6" zeilen={8} spalten={1}
                 knoepfe={bridge.boneditKnoepfe || []} style={S.bonedit}
                 knopfStyle={S.boneditKnopf} />
      {/* 7 bonfnc */}
      <PushPanel bridge={bridge} pfad="7" zeilen={4} spalten={2}
                 knoepfe={bridge.bonfncKnoepfe || []} style={S.bonfnc} />
      {/* 8 bonContainer - die Klasse setzt mainpos.js zur Laufzeit auf
          "BonContainer CashWindow" (prepare), nicht die XML allein. */}
      <Frame bridge={bridge} pfad="8" addclass="BonContainer CashWindow" style={S.bonContainer}>
        <DataGrid bridge={bridge} pfad="8:0" style={S.gitter} />
        <Pre bridge={bridge} pfad="8:1" style={S.bonSum} />
      </Frame>
      {/* 9 info */}
      {/* onBlur-Handler und sein Parameter gehoeren an die JSX, nicht in
          mountLegacy: _handle setzt properties.events bei jedem Rendern neu,
          ein nachtraeglich angehaengter Handler waere sofort wieder weg. */}
      <InputField bridge={bridge} pfad="9" addclass="CMainPOSInput" style={S.info}
                  backspace clear inputProperties={{ type: 'Money' }}
                  inputEvents={{ onBlur: bridge.restoreInputFocus }}
                  inputAttributes={{ onBlur_parameter_0: bridge.id }} />
      {/* 10 sumcaption / 11 suminfo */}
      {/* "Text Label" ist die VOREINSTELLUNG des Legacy-CTextLabel
          (components/textlabel.js:19) und steht im leeren Hauptbildschirm
          tatsaechlich da - CBonManager ueberschreibt sie erst beim ersten
          Bon. Ein leerer Text waere hier also falsch. */}
      <TextLabel bridge={bridge} pfad="10" addclass="sumcaption" ausHandle />
      <TextLabel bridge={bridge} pfad="11" addclass="suminfo" ausHandle />
      {/* 12 statusBar */}
      <Frame bridge={bridge} pfad="12" style={S.statusBar}>
        {/* SchreibCaption statt Caption: auf diese drei schreibt Legacy-Code
            per prepare() und reValue() (updateDateTime alle 40s). Sie lesen
            aus demselben Handle, in das jene schreiben - siehe widgets.jsx. */}
        <SchreibCaption bridge={bridge} pfad="12:0" style={S.userInfo} />
        <SchreibCaption bridge={bridge} pfad="12:1" style={S.versionInfo} />
        <SchreibCaption bridge={bridge} pfad="12:2" style={S.dateTimeInfo} />
      </Frame>
      {/* 13 infoIcons */}
      {/* spalten 7 -> 6 (26.8.2026): das Funkscanner-Symbol ist entfallen.
          Das Raster ist FEST - ohne die Anpassung bliebe eine leere Zelle
          stehen (live nachgemessen: 7 Kinder, das letzte ohne Bild).
          ACHTUNG: In JSX ist "//" KEIN Kommentar, sondern TEXT. Genau so
          stand dieser Hinweis kurzzeitig sichtbar auf dem Kassenschirm - und
          wurde beim Neuaufnehmen sogar in die Baselines uebernommen. */}
      <PushPanel bridge={bridge} pfad="13" zeilen={1} spalten={6}
                 knoepfe={bridge.infoIconKnoepfe || []} addclass="infoicons"
                 knopfStyle={S.infoIconKnopf} />
      {/* 14 war das POSysseus-Logo (dauerhaft versteckt). Am 26.8.2026
          entfallen - der Altname soll nirgends mehr vorkommen. Der Platz
          bleibt als LETZTES Kind unbesetzt, die Nummerierung der uebrigen
          Kinder aendert sich dadurch nicht. */}
    </>
  );
}

// RISIKO 2 des Runbooks: 1691 der 2020 Elemente (84 %) sind die
// Produktauswahl. bridge.update() rendert IMMER von der Wurzel - ohne diese
// Kapselung zoege jede Bon-Aenderung alle 730 Knoepfe mit, und das sechsfach,
// wenn sechs Benutzer angemeldet sind (gemessen: 12138 Elemente).
// "bridge" taugt NICHT als memo-Prop (Identitaet konstant), deshalb der
// Versionszaehler "stand" - den zaehlt reDrawUeberReact ohnehin hoch.
const ProdAuswahl = memo(function ProdAuswahl({ bridge, pfad }) {
  return (
    <TabbedPushPanel bridge={bridge} pfad={pfad}
                     reiter={bridge.reiterKnoepfe || []}
                     inhalte={bridge.inhaltKnoepfe || []}
                     reiterStyle={S.reiter} reiterZeilen={3} reiterSpalten={4}
                     inhaltStyle={S.inhalte} inhaltZeilen={10} inhaltSpalten={4}
                     inhaltArt="xy" />
  );
}, (alt, neu) => alt.stand === neu.stand);

// --- Legacy-Verhalten, WOERTLICH uebernommen ------------------------------
// Die 70 Prototype-Methoden aus screens/mainpos.js, maschinell extrahiert
// (nicht abgetippt) und unveraendert - "this" ist hier die Bridge. Sie sind
// VERHALTEN, nicht Darstellung, und bleiben deshalb wie sie sind.
//
// Nicht dabei ist prepare(): dessen erster Teil baut die Kinder aus der
// Layout-XML, und genau das ersetzt React. Der asynchrone Rest steht unten
// in spec.prepare neu.
//
// Die Methoden greifen auf GLOBALE Klassen zu (CPushButton, CXmlHandler,
// CPosReceiptList, ...), die legacy-bridge.js veroeffentlicht - genauso wie
// bisher in mainpos.js. Deshalb sind hier keine zusaetzlichen Importe noetig.
//
// Vollstaendigkeit geprueft: ausserhalb der 71 Methodenbloecke enthaelt
// mainpos.js nur Kopfkommentar, Import, Konstruktor, Prototyp-Zuweisung und
// Export - es wurde nichts abgeschnitten und nichts uebersehen.
//
// NICHT uebernommen: properties.disableType = "OVERLAY" aus dem Konstruktor.
// Einziger Leser ist CGFX.disableMe (lib/scripts/CLS_CGFX.js:264), und jede
// Referenz auf CGFX in components/component.js ist auskommentiert - die
// Eigenschaft ist toter Code ohne Wirkung.

const legacy = {};

// Artikel-Reiter und -Knoepfe NEU aus der DB holen und rendern - fuer den
// Datenimport (dataimport.js reDrawAllMainPOS). Dort stand bisher der
// Legacy-Weg prodSelect.prepare() + reDraw(): prepare() baute echte, nie
// gezeichnete Panels an das React-Handle und warf (Code-Suche 30.9.2026),
// und selbst ohne Fehler haette React die neuen Knoepfe nie gesehen - es
// rendert aus reiterKnoepfe/inhaltKnoepfe. Gleicher Ablauf wie im Aufbau
// (prepare, COL == -1), danach neu rendern und die Panels neu verdrahten.
// Liefert ein jQuery-Deferred (Runbook Regel 4).
legacy.produkteNeuLaden = function() {
    var bridge = this;
    var def = jQuery.Deferred();
    $.when(bridge.makeProdCatButtons()).then(function (ProdCatButtons) {
        var its = new Array(ProdCatButtons.length);
        var inhaltLegacy = new Array(ProdCatButtons.length);
        var iterator = function it(i) {
            if (i < its.length) {
                $.when(bridge.makeProdButtons(i)).then(function (ProdButtons) {
                    inhaltLegacy[i] = ProdButtons;
                    its[i] = knoepfeAusLegacy(ProdButtons);
                    iterator(i + 1);
                });
                return;
            }
            bridge.reiterLegacy = ProdCatButtons;
            bridge.reiterKnoepfe = knoepfeAusLegacy(ProdCatButtons);
            bridge.inhaltLegacy = inhaltLegacy;
            bridge.inhaltKnoepfe = its;
            bridge.update({ prodStand: (bridge.state.prodStand || 0) + 1 });
            verdrahteTabbedPushPanel(bridge, '3', bridge.reiterKnoepfe.length);
            def.resolve();
        };
        iterator(0);
    });
    return def.promise();
}

legacy.drawSubmenus = function() {
    this.cmpOtherFunc = this.addChild(COtherFunctions);
    this.cmpOtherFunc.properties.styles.visibility = "hidden";
    this.cmpOtherFunc.setMain(this);
    this.cmpBKFunc = this.addChild(CBKFunctions);
    this.cmpBKFunc.properties.styles.visibility = "hidden";
    this.cmpBKFunc.setMain(this);
    this.cmpChanges = this.addChild(CChangesPanel);
    this.cmpChanges.properties.styles.visibility = "hidden";
    this.cmpChanges.setMain(this);
    this.cmpInventory = this.addChild(CInventoryPanel);
    this.cmpInventory.properties.styles.visibility = "hidden";
    this.cmpInventory.setMain(this);
    this.cmpCashbox = this.addChild(CCashboxPanel);
    this.cmpCashbox.properties.styles.visibility = "hidden";
    this.cmpCashbox.setMain(this);
    this.cmpUserFunc = this.addChild(CUserFunctions);
    this.cmpUserFunc.properties.styles.visibility = "hidden";
    this.cmpUserFunc.setMain(this);

    //this.cmpMessageFunc = this.addChild(MessageFunctions);
   // this.cmpMessageFunc.properties.styles.visibility = "hidden";
    //this.cmpMessageFunc.setMain(this);

    this.cmpOtherFunc.draw();
    this.cmpBKFunc.draw();
    this.cmpChanges.draw();
    this.cmpInventory.draw();
    this.cmpCashbox.draw();
    this.cmpUserFunc.draw();
    //this.cmpMessageFunc.draw();
}

legacy.updateNewEMails = function(me) {
    /*privMan.enablePrivilege (privList);*/
    if (me.userProps && me.userProps.Login) {
        $.when(new CEMail()).done(function(haveemail) {
            // Load Payment Types from DB
            if (haveemail) {
                var sql = "SELECT EMAIL_ID FROM EMAILS WHERE U_ID = " + me.userProps.Login +
                    " AND Read = 0 AND Deleted = 0";
                $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
                    if (rows && rows.length > 0)
                        me.InfoIcons.highlightIcon("email", me.UID);
                });
            }
        });
    }

    //r.free();
}

legacy.restoreInputFocus = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (POS.sessions.currentSession == me.UID) {
        me.components.info.input.setFocus();
        me.inputFocusEnabled = true;
    }
}

legacy.disableInputFocus = function() {
    this.components.info.input.properties.events.onBlur = null;
    this.inputFocusEnabled = false;
}

legacy.enableInputFocus = function() {
    this.components.info.input.properties.events.onBlur = this.restoreInputFocus;
    this.components.info.input.properties.attributes.onBlur_parameter_0 = this.id;
    this.components.info.input.setFocus();
    this.inputFocusEnabled = true;
}

legacy.getDateTime = function() {
    var d = new Date();
    var minutes = d.getMinutes();
    var hours = d.getHours();

    var dateTimeToShow = d.getDate() + "." + (d.getMonth() + 1) + "." +
        (d.getYear() + 1900) + " - " + expandInteger(hours, 2) + ":" +
        expandInteger(minutes, 2);

    if (typeof this.userProps["COL"] != "undefined" && this.userProps["COL"] == -1) {
        if (minutes == 0 || minutes == 30) { // Strict-Mode-Fix (ESM): Oktal-Literal 00 -> 0 (wertgleich)
            if (d.getSeconds() < 20)
                this.InfoIcons.flagLastRebatesUpdate = false;
        }

        /*if (!this.InfoIcons.flagLastRebatesUpdate) {
        	var day = d.getDay() - 1;
        	if (day < 0)
        		day = 6;
        	var halfHour = hours * 2 + (minutes > 29 ? 1 : 0);
        	this.BonManager.checkForRebateChanges(day, halfHour, hours, minutes);
        	this.InfoIcons.flagLastRebatesUpdate = true;
        }*/
    }

    return dateTimeToShow;
}

legacy.testInput = function(me) {
    try {
        if (POS.sessions.currentSession != me.UID) {
            if (me.inputFocusEnabled) {
                me.disableInputFocus();
                me.inputFocusEnabled = false;
            }
            return;
        } else {
            if ((typeof me.cmpBalance == "undefined" || !me.cmpBalance.transmitted) &&
                (typeof me.cmpCustomer == "undefined" || !me.cmpCustomer.transmitted) &&
                (!me.cmpCopies || typeof me.cmpCopies == "undefined" || !me.cmpCopies.transmitted)) {
                if (!me.components.info.input.properties.events.onBlur || !me.inputFocusEnabled) {
                    for (var j in POS.sessions.openSessions) {
                        if (j != 0) {
                            var sess = POS.sessions.openSessions[j];
                            sess.disableInputFocus();
                        }
                    }
                    me.enableInputFocus();
                    log.add(2, "Info", "Restored focus to input field for user " + me.userProps["Login"]);
                }
            }
        }
    } catch (e) {
        log.add(2, "Error", "testInput failed for user " + me.UID, e);
    }
}

legacy.confirmInput = function() {
    try {
        for (var j in POS.sessions.openSessions) {
            if (j != 0) {
                var sess = POS.sessions.openSessions[j];
                sess.disableInputFocus();
            }
        }
        this.enableInputFocus();
    } catch (e) {
        log.add(2, "Error", "confirmInput failed for user " + this.UID, e);
    }
}

legacy.updateDateTime = function(me) {
    me.components.statusBar.components.dateTimeInfo.properties.value = me.getDateTime();
    me.components.statusBar.components.dateTimeInfo.reValue();
}

legacy.makePaymentButtons = function() {

    var paybtns = new Array();
    var btns = new Array();
    var btns2 = new Array();
    var dfd = jQuery.Deferred();
    /*privMan.enablePrivilege (privList);*/
    var MainPOSClass = this;
    // Load Payment Types from DB
    var sql = "SELECT PAY_ID, Name, Picture FROM PAY WHERE Shortcut != 'PPC' ORDER BY PAY_ID";
    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        if (rows.length < 1)
        //if (r.recordCount() < 1)
        {
            //r.free();
            dfd.resolve(Array());
            return;
            //return Array();
        }

        var BigButtonCount = 3;

        for (var i = 0; i < Math.min(BigButtonCount, rows.length); i++) {

            btns[i] = new CPushButton(MainPOSClass.id, "PaymentButton1");
            btns[i].properties.caption.value = rows[i].Name;
            btns[i].properties.events.onMousedown = MainPOSClass.payment;
            //btns[i].properties.styles.width = "49%";
            //btns[i].properties.styles.paddingLeft = "40%";
            btns[i].properties.attributes.onMousedown_parameter_0 = MainPOSClass.id;
            btns[i].properties.attributes.onMousedown_parameter_1 = rows[i].PAY_ID;
            btns[i].properties.styles.backgroundRepeat = "no-repeat";
            //if (i < 6) {
            if (rows[i].Picture) {
                var picture = "url(lib/img/functions/" + rows[i].Picture + ")";
                btns[i].properties.styles.backgroundImage = picture;
            }

            //r.moveNext();
        }

        for (var i = BigButtonCount; i < Math.min(10, rows.length); i++) {

            btns2[i - BigButtonCount] = new CPushButton(MainPOSClass.id, "PaymentButton2");
            btns2[i - BigButtonCount].properties.caption.value = rows[i].Name;
            btns2[i - BigButtonCount].properties.events.onMousedown = MainPOSClass.payment;
            btns2[i - BigButtonCount].properties.styles.width = "45%"; //"71px";

            if (i == (6 - BigButtonCount))
                btns2[i - BigButtonCount].setEnabled(true);
            btns2[i - BigButtonCount].properties.attributes.onMousedown_parameter_0 = MainPOSClass.id;
            btns2[i - BigButtonCount].properties.attributes.onMousedown_parameter_1 = rows[i].PAY_ID;
            btns2[i - BigButtonCount].properties.styles.backgroundRepeat = "no-repeat";

        }
        paybtns[0] = btns;
        paybtns[1] = btns2;
        //r.free();
        dfd.resolve(paybtns);
        //return btns;
    });
    return dfd.promise();
}

legacy.makeBonFncButtons = function() {
    var xml = new CXmlHandler("etc/layout/LYT_bonfncButtons.xml");

    var btns = new Array();

    var xmlBtns = xml.rootElement.getElementsByTagName("button");

    for (var n = 0; n < xmlBtns.length; n++) {
        btns[n] = new CPushButton(this.id, "BonFncBtn");
        btns[n].properties = combine(btns[n].properties,
            xml.getObject(xmlBtns[n], false)["button"]);
        btns[n].properties.attributes.onMousedown_parameter_0 = this.id;
    }

    btns[0].properties.events.onMousedown = this.cancelBon;
    btns[2].properties.events.onMousedown = this.bonPark;
    btns[2].properties.attributes.onMousedown_parameter_0 = this.id;
    btns[4].properties.events.onMousedown = this.globalRebate;
    btns[5].properties.events.onMousedown = this.paydown;
    btns[6].properties.events.onMousedown = this.bonCopies;
    btns[7].properties.events.onMousedown = this.productCategories;
    btns[7].properties.attributes.onMousedown_parameter_1 = "REC";

    return btns;
}

legacy.makeLeftPadButtons = function() {
    var xml = new CXmlHandler("etc/layout/LYT_leftPanelButtons.xml");

    var btns = new Array();

    var xmlBtns = xml.rootElement.getElementsByTagName("button");

    for (var n = 0; n < xmlBtns.length; n++) {
        btns[n] = new CPushButton(this.id, "LeftPadBtn");
        btns[n].properties = combine(btns[n].properties,
           xml.getObject(xmlBtns[n], false)["button"]);
        btns[n].properties.attributes.onMousedown_parameter_0 = this.id;
        //btns[n].properties.styles.backgroundColor = "#EEEEEE";
    }

    btns[0].properties.events.onMousedown = this.otherFunctions;
    //    btns[1].properties.events.onMousedown = this.paydown;
    btns[1].properties.events.onMousedown = this.infoMode;
    btns[1].properties.attributes.onMousedown_parameter_1 = "screen";
    btns[2].properties.events.onMousedown = this.msg;
    btns[3].properties.events.onMousedown = this.changes;
    btns[4].properties.events.onMousedown = this.BKFunctions;
    btns[5].properties.events.onMousedown = this.inventory;
    btns[6].properties.events.onMousedown = this.customerAdmin;
    btns[7].properties.events.onMousedown = this.cashbox;
    // NUMMERIERUNG: Der Funkscanner sass auf Index 8 und ist am 26.8.2026
    // entfallen - alle folgenden Tasten ruecken um eins auf. Das ist keine
    // Kosmetik: die Handler haengen ALLEIN am Index, ein Versehen wuerde sie
    // still auf die falschen Tasten legen. Beim ersten Versuch fehlte genau
    // diese Anpassung, und die Kassa starb sofort mit "Cannot read properties
    // of undefined" an btns[12].
    btns[8].properties.events.onMousedown = this.userFunctions;
    btns[9].properties.events.onMousedown = this.transferPOS;

    if (POS.isPOSOpened) {
        btns[10].properties.caption.value = POS.lang.getText("BUTTONS", "END_BALANCE");
        btns[10].properties.events.onMousedown = this.endPOS;
    } else {
        btns[10].properties.caption.value = POS.lang.getText("BUTTONS", "START_BALANCE");
        btns[10].properties.events.onMousedown = this.startPOS;
    }

    btns[11].properties.events.onMousedown = this.logout;

    return btns;
}


legacy.paydown = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (!me.BonManager.currentBonId) {
        log.add(4, "Info", "Loading parked-bons list", "");

        me.cmp = me.addChild(CPosReceiptList);
        me.cmp.Main = me;
        me.cmp.mode = "parked";
        me.cmp.showPaydown = true;
        $.when(me.cmp.buildArrayBons(true)).then(function(val) {
            if (val)

            //if (me.cmp.buildArrayBons(true))
            {
                var text = POS.lang.getText("MESSAGES", "BON_LIST_LOAD");
                POS.hourGlass.show(text, me, me.doPaydown);
            } else {
                text = POS.lang.getText("MESSAGES", "NO_PAYDOWN_BONS");
                POS.message.show(text, "warning", "OK");
            }
        });
        return;
    }

    var openSum = me.BonManager.getSumArray()[6];
    var userAmount = remEuropeanComma(me.components.info.input.properties.attributes.value);
    userAmount = parseFloat(userAmount);

    if (!userAmount) {
        var text = POS.lang.getText("MESSAGES", "NO_AMOUNT");
        POS.message.show(text, "warning", "");
        return;
    }

    if (userAmount >= openSum) {
        var text = POS.lang.getText("MESSAGES", "TOO_MUCH_PAYDOWN");
        POS.message.show(text, "warning", "");
        return;
    }
    log.add(4, "Info", "Entering paydown mode", "");

    me.cmpOnlyPay = me.addChild(COnlyPayMode);
    me.cmpOnlyPay.Main = me;
    me.cmpOnlyPay.draw();
}

legacy.doPaydown = function(me) {
    me.cmp.draw();
    POS.hourGlass.hide();
}

legacy.msg = function(component, parameters) {
    /*privMan.enablePrivilege (privList);*/

    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "IB") {
    /*    me.cmpMessageFunc.properties.styles.visibility = "visible";
        me.cmpMessageFunc.reStyle();
        me.cmpMessageFunc.setCallback();*/
		var m  = new MessageFunctions();
		m.init((POS.POSType=='Master')?POS.POSId:POS.MasterID,POS.sessions.currentSession);
        me.lastSubmenuId = "IB";
    } else
        me.lastSubmenuId = 0;
}

legacy.otherFunctions = function(component, parameters) {
    /*privMan.enablePrivilege (privList);*/

    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "OF") {
       /* me.cmpOtherFunc.properties.styles.visibility = "visible";
        me.cmpOtherFunc.reStyle();*/
	document.getElementById(me.cmpOtherFunc.id).style.visibility="visible";
       
       me.cmpOtherFunc.setCallback();
        me.lastSubmenuId = "OF";
    } else
        me.lastSubmenuId = 0;
}

legacy.restoreLastSubmenuId = function(me) {
    me.lastSubmenuId = 0;
}

legacy.BKFunctions = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "BK") {
        /*me.cmpBKFunc.properties.styles.visibility = "visible";
        me.cmpBKFunc.reStyle();*/
 document.getElementById(me.cmpBKFunc.id).style.visibility="visible";
        me.cmpBKFunc.setCallback();
        me.lastSubmenuId = "BK";
    } else
        me.lastSubmenuId = 0;
}

legacy.inventory = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "INV") {
        /*me.cmpInventory.properties.styles.visibility = "visible";
        me.cmpInventory.reStyle();*/
 document.getElementById(me.cmpInventory.id).style.visibility="visible";

        me.cmpInventory.setCallback();
        me.lastSubmenuId = "INV";
    } else
        me.lastSubmenuId = 0;
}

legacy.cashbox = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "CB") {
        /*me.cmpCashbox.properties.styles.visibility = "visible";
        me.cmpCashbox.reStyle();*/
        document.getElementById(me.cmpCashbox.id).style.visibility="visible";
	me.cmpCashbox.setCallback();
        me.lastSubmenuId = "CB";
    } else
        me.lastSubmenuId = 0;
}

legacy.userFunctions = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "UF") {
	document.getElementById(me.cmpUserFunc.id).style.visibility = "visible";
      /*  me.cmpUserFunc.properties.styles.visibility = "visible";
        me.cmpUserFunc.reStyle();*/
        me.cmpUserFunc.setCallback();
        me.lastSubmenuId = "UF";
    } else
        me.lastSubmenuId = 0;
}

legacy.changes = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.lastSubmenuId != "CH") {
        /*me.cmpChanges.properties.styles.visibility = "visible";
        me.cmpChanges.reStyle();*/
	document.getElementById(me.cmpChanges.id).style.visibility="visible";

        me.cmpChanges.setCallback();
        me.lastSubmenuId = "CH";
    } else
        me.lastSubmenuId = 0;
}

legacy.infoMode = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    var from = parameters[1];

    if (typeof me.infoCmp == "undefined" || !me.infoCmp.transmitted) {
        log.add(4, "Info", "Price-information mode on", "");

        me.infoCmp = me.addChild(CMainPOSInfoMode);
        me.infoCmp.mode = "info";
        me.infoCmp.Main = me;
        me.infoCmp.draw();
    }
     document.getElementById(me.infoCmp.id).style.visibility="visible";


}

legacy.bonPark = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.BonManager.currentBonId != 0) {
        if (me.BonManager.currentCustomerId != 0)
            me.BonManager.updateCustomerBon();
        me.BonManager.reset();
        log.add(4, "Info", "Bon parked", "");
    } else {
        log.add(4, "Info", "Loading parked-bons list", "");

        me.cmp = me.addChild(CPosReceiptList);
        me.cmp.Main = me;
        me.cmp.mode = "parked";
        me.cmp.showPaydown = false;
        $.when(me.cmp.buildArrayBons()).then(function(val) {
            if (val)
            //if (me.cmp.buildArrayBons(true))
            {
                var text = POS.lang.getText("MESSAGES", "BON_LIST_LOAD");
                POS.hourGlass.show(text, me, me.doBonPark);
            } else {
                text = POS.lang.getText("MESSAGES", "NO_PARKED_BONS");
                POS.message.show(text, "warning", "OK");
                return;
            }
        });
    }
}

legacy.doBonPark = function(me) {
    me.cmp.draw();
	var el = document.getElementById(me.cmp.id);
    //el.getElementsByClassName("CNumPad")[0].style.visibility="hidden";
    POS.hourGlass.hide();
}

legacy.productCategories = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    var mode = parameters[1];

    var amount = remEuropeanComma(me.components.info.input.properties.attributes.value);

    if (!amount) {
        var text = POS.lang.getText("MESSAGES", "NO_AMOUNT");
        POS.message.show(text, "warning", "OK");
        return;
    }

    if (mode == "REC") {
        var wholeAmount = 0;

        for (var Cnt in me.BonManager.currentBon) {
            var Entry = me.BonManager.currentBon[Cnt];
            if (Entry.type == "REC" && !Entry.cancelled)
                wholeAmount += Entry.amount;
        }

        if (wholeAmount - amount < -parseFloat(POS.EnvProperties["MAXCOMPLAINAMOUNT"])) {
            var text = POS.lang.getText("MESSAGES", "TOO_MUCH_AMOUNT");
            POS.message.show(text, "warning", "OK");
            return;
        }

        log.add(4, "Info", "product categories (complains) on", "");
    }

    me.cmpProdCat = me.addChild(CProductCategoriesMode);
    me.cmpProdCat.amount = amount;
    me.cmpProdCat.mode = mode;
    me.cmpProdCat.draw();
}

legacy.bonCopies = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Loading boncopies list", "");

    var text = POS.lang.getText("MESSAGES", "BON_LIST_LOAD");
    POS.hourGlass.show(text, me, me.doBonCopies);
}

legacy.doBonCopies = function(me) {
    me.cmpCopies = me.addChild(CPosReceiptList);
    me.cmpCopies.mode = "copies";
    me.cmpCopies.Main = me;
    $.when(me.cmpCopies.draw()).done(function() {
        me.cmpCopies.components.inputField.input.setFocus();
        POS.hourGlass.hide();
    });
}

legacy.manualEANCode = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Adding product from EAN-Code", "");
    var code = me.vacuumInfoValue(me);

    var barcodeReader = new CBarcodeReader();
    barcodeReader.processInput(code, me, "screen");
}

legacy.prepaidCardPayment = function(cardNumber) {
    var MainPOSClass = this;
    $.when(this.BonManager.checkCustomerThresholdPrice()).then(function(thresholdBonParams) {
        MainPOSClass.prepaidCardNumber = cardNumber;
        if (thresholdBonParams.length > 0) {
            var moneyToGo = thresholdBonParams[0];
            var rebatePercent = thresholdBonParams[1];
            var text = POS.lang.getText("MESSAGES", "THRESHOLD_BON_1").replace("%amount%", float2StringD2(moneyToGo));
            text += POS.lang.getText("MESSAGES", "THRESHOLD_BON_2").replace("%percent%", float2StringD2(rebatePercent));
            POS.message.show(text, "warning", "YESNO", MainPOSClass, MainPOSClass.doPrepaidCardPayment);
        } else
            MainPOSClass.doPrepaidCardPayment(MainPOSClass, "YES");
    });
}

legacy.doPrepaidCardPayment = function(me, result) {
    if (result != "YES")
        return;

    log.add(4, "Info", "Entering prepaid cards payment", "");

    if (me.cmpPrepaidPayment != null && me.cmpPrepaidPayment.transmitted) {
        var text = POS.lang.getText("MESSAGES", "PLEASE_CLOSE_PPC_MODE_FIRST");
        POS.message.show(text, "warning", "OK");
        return;
    }

    me.cmpPrepaidPayment = me.addChild(CPrepaidCardPayment);
    me.cmpPrepaidPayment.cardNumber = me.prepaidCardNumber;
    me.cmpPrepaidPayment.draw();
    me.cmpPrepaidPayment.makeNoteButtons();
    me.cmpPrepaidPayment.initPayment();
    document.getElementById(me.cmpPrepaidPayment.id).style.visibility="visible";
    
}

legacy.makeProdCatButtons = function() {
    /*privMan.enablePrivilege (privList);*/
    var dfd = jQuery.Deferred();
    var btns = new Array();
    var MainPOSClass = this;
    var sql = "SELECT Text1, Text2, Text3, Icon, Color, LineColor, LineWeight, " +
        "Flash, Col, Row, Page FROM BTN WHERE Parent_BTN_ID = 0 ORDER BY BTN_ID";
    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        //for (var i = 0; i < r.recordCount(); i++)
        for (var i = 0; i < rows.length; i++) {
            btns[i] = new CPushButton(MainPOSClass.id, "ProdCatButton");

            var Text1 = rows[i].Text1;
            var Text2 = rows[i].Text2;
            var Text3 = rows[i].Text3;
            var Color = rows[i].Color;
            var Icon = rows[i].Icon;
            var Flash = rows[i].Flash;
            var LineColor = rows[i].LineColor;
            var LineWeight = rows[i].LineWeight;
            var Col = rows[i].Col;
            var Row = rows[i].Row;
            var Page = rows[i].Page;

            btns[i].properties.styles.backgroundColor = "#839AE0";
            btns[i].properties.styles.borderColor = "#7282B1";
            btns[i].properties.caption.value = (Text1 ? Text1 : "") +
                (Text2 ? ("<br>" + Text2) : "") + (Text3 ? ("<br>" + Text3) : "");
            if (Color != "0.0.0" && Color != "0") {
                var ColArray = Color.split(".");
                Color = "#" + decToHexa(ColArray[0]) + decToHexa(ColArray[1]) +
                    decToHexa(ColArray[2]);
                btns[i].properties.styles.backgroundColor = Color;
            }

            if (LineColor != "0.0.0" && LineColor != "0") {
                ColArray = LineColor.split(".");
                LineColor = "#" + decToHexa(ColArray[0]) + decToHexa(ColArray[1]) +
                    decToHexa(ColArray[2]);
                btns[i].properties.styles.borderColor = LineColor;
            }
            btns[i].properties.styles.borderWidth = (LineWeight ? (LineWeight + "px") :
                "2px");
            btns[i].properties.styles.backgroundImage = (Icon ? Icon : null);

            //r.moveNext();
        }
        //r.free();
        //return btns;
        dfd.resolve(btns);
    });
    return dfd.promise();
}

legacy.makeProdButtons = function(group) {
    /*privMan.enablePrivilege (privList);*/
    var MainPOSClass = this;
    var dfd = jQuery.Deferred();
    var sql = "SELECT BTN_ID, Text1, Text2 FROM BTN WHERE Parent_BTN_ID = 0";
    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        //  for (var i = 0; i < group; i++){}
        //r.moveNext();
        var i = group;
        var groupId = rows[i].BTN_ID;
        var groupName = rows[i].Text1;
        var text2 = rows[i].Text2;
        if (text2)
            groupName += " " + text2;

        sql = "SELECT Text1, Text2, Text3, Icon, Color, LineColor, LineWeight, " +
            "Flash, PROD_ID, Page, Col, Row, FreePrice FROM BTN WHERE " +
            "Parent_BTN_ID = " + groupId + " AND PROD_ID != 0";
        $.when(POS.SQLiteCon.execute(sql)).then(function(rows2) {
            var btns = new Array();

            for (i = 0; i < rows2.length; i++) {
                btns[i] = new CPushButton(MainPOSClass.id, "ProdButtons");

                var Text1 = rows2[i].Text1;
                var Text2 = rows2[i].Text2;
                var Text3 = rows2[i].Text3;
                var Color = rows2[i].Color;
                var Icon = rows2[i].Icon;
                var Flash = rows2[i].Flash;
                var LineColor = rows2[i].LineColor;
                var LineWeight = rows2[i].LineWeight;
                var Page = rows2[i].Page;
                var Col = rows2[i].Col;
                var Row = rows2[i].Row;
                var FreePrice = parseFloat(rows2[i].FreePrice);

                //var gfx = new CGFX();

                btns[i].properties.caption.value = (Text1 ? Text1 : "") +
                    (Text2 ? ("<br>" + Text2) : "") + (Text3 ? ("<br>" + Text3) : "");
                if (Color != "0.0.0" && Color != "0") {
                    var ColArray = Color.split(".");
 		    Color = "#" + decToHexa(ColArray[0]) + decToHexa(ColArray[1]) +
                    decToHexa(ColArray[2]);
                    btns[i].properties.styles.backgroundColor = Color;
                } else {
                    //var ColArray = gfx.hex2Color("#9EE083");
                    btns[i].properties.styles.backgroundColor = "#9EE083";
                }

                //LineColor = gfx.color2Hex(gfx.brighten(ColArray, -0.25));

                btns[i].properties.styles.borderColor = LineColor;

                btns[i].properties.page = Page;
                btns[i].properties.xPos = Col;
                btns[i].properties.yPos = Row;

                //        btns[i].properties.styles.borderWidth = (LineWeight ? (LineWeight + "px") :
                //            "2px");
                // ESM-Welle 5a: Diese beiden Zuweisungen waren IMMER wirkungslos.
                // properties.caption.styles kommt aus etc/defaults/LYT_CPushButton.xml,
                // wo <styles> nur Whitespace enthaelt -> CXmlHandler.getObject liefert
                // dafuer einen STRING ("\n\t "), kein Objekt. Im Sloppy-Mode wurde die
                // Zuweisung auf ein Primitive still verworfen; im Modul (immer strict)
                // wirft sie TypeError. Stilllegen ist daher WERTGLEICH zum Ist-Verhalten -
                // sie zu "reparieren" wuerde die GUI aendern (Pixelgleichheit, Regel 1).
                // Das aeussere properties.styles ist dagegen ein echtes Objekt (sein
                // <styles>-Element enthaelt einen XML-Kommentar als Kindknoten).
                // btns[i].properties.caption.styles.fontWeight = (LineWeight ? "normal" : "bold");
                // btns[i].properties.caption.styles.fontWeight = (Flash ? "none" : "blink");
                btns[i].properties.styles.backgroundImage = (Icon ? Icon : null);
                if (FreePrice == 0) {
                    btns[i].properties.events.onMousedown = MainPOSClass.addProd;
                    btns[i].properties.attributes.onMousedown_parameter_2 = "screen";
                } else if (FreePrice == 1) {
                    btns[i].properties.events.onMousedown = MainPOSClass.addProdWithPrice;
                    if (Page == 1 && Col == 4 && Row == 10)
                        btns[i].properties.attributes.onMousedown_parameter_2 = groupName;
                    else
                        btns[i].properties.attributes.onMousedown_parameter_2 = Text1;
                }
                btns[i].properties.attributes.onMousedown_parameter_0 = MainPOSClass.id;
                btns[i].properties.attributes.onMousedown_parameter_1 = rows2[i].PROD_ID;
                //r.moveNext();
            }
            // r.free();
            //return btns;
            dfd.resolve(btns);
        });
    });
    return dfd.promise();
}

legacy.addProdWithPrice = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    var prodId = parameters[1];
    var prodName = parameters[2];

    log.add(4, "Info", "Adding product with price '" + prodId + "'", "");

    if (me.BonManager.displayMode != "normal") {
        if (me.BonManager.displayMode == "info") {
            var parameters = new Array(POS.lang.getText("LABELS", "PRODUCT_WITHOUT_PRICE"), null, null);
            me.infoCmp.showPrice(parameters);
        } else if (me.BonManager.displayMode == "stock")
            me.BonManager.preAddEntryPOS(1, prodId);
        return;
    }

    me.cmpPriceInput.mode = "freeprod";
    me.cmpPriceInput.type = new Array(prodId, prodName, me.vacuumInfoValue(me));
    me.cmpPriceInput.show();
    log.add(4, "Info", "Mode 'PriceInput' entered succesfully", "");
}

legacy.addFree = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    var groupId = parameters[1];
    var groupName = parameters[2];

    var cmp = me.addChild(CPriceInputMode);
    cmp.mode = "freegroup";
    cmp.type = new Array(groupId, groupName);
    cmp.draw();
}

legacy.logout = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var cannotLogout;

    if (!POS.isPOSOpened || me.userProps["POC"] == 0)
        cannotLogout = false;
    else
        cannotLogout = POS.sessions.isLastWithClosePOSRights(me.UID);

    if (cannotLogout) {
        var text = POS.lang.getText("MESSAGES", "FIRST_CLOSE_CASHBOX");
        POS.message.show(text, "warning", "OK");
        return;
    }

    me.timeRecord();
}

legacy.timeRecord = function() {
    var text = POS.lang.getText("MESSAGES", "TIME_RECORD_LOAD");

    log.add(4, "Info", "Time record on", "");
    POS.hourGlass.show(text, this, this.doTimeRecord);
}

legacy.doTimeRecord = function(me) {
    var cmp = me.addChild(CTimeRecord);
    cmp.userId = POS.sessions.currentSession;
    cmp.userName = me.userProps["Name"];
    cmp.draw();

    POS.hourGlass.hide();
}

legacy.startPOS = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.userProps["POP"] == 0) {
        var text = POS.lang.getText("MESSAGES", "NO_RIGHT_OPEN_POS");
        POS.message.show(text, "warning", "");
        return;
    }

    if (POS.isPOSBeingOpened) {
        var text = POS.lang.getText("MESSAGES", "CASHBOX_ALREADY_BEING_OPENED");
        POS.message.show(text, "warning", "OK");
        return;
    }

    POS.isPOSBeingOpened = true;

    var text = POS.lang.getText("MESSAGES", "OPEN_CASHBOX_LOAD");
    POS.hourGlass.show(text, me, me.doStartPOS);
}

legacy.doStartPOS = function(me) {
    me.cmpBalance = me.addChild(CPosBalancing);
    me.cmpBalance.mode = "start";
    me.cmpBalance.draw();
    me.cmpBalance.inputs[0].field.input.setFocus();

    POS.hourGlass.hide();
}

legacy.transferPOS = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.userProps["TRP"] == 0) {
        var text = POS.lang.getText("MESSAGES", "NO_RIGHT_TRANSFER_POS");
        POS.message.show(text, "warning", "");
        return;
    }

    /*    var usersWithRight = 0;
    for (var j in POS.sessions.openSessions){
    if (j == 0)
    continue;
    if (POS.sessions.openSessions[j].userProps["POP"] != 0)
    usersWithRight ++;
    }
    if (usersWithRight < 2){
    var text = POS.lang.getText("MESSAGES", "POS_TRANSFER_NOT_POSSIBLE");
    POS.message.show (text, "warning", "OK");
    return;
    }*/

    me.cmpList = me.addChild(CPosReceiptList);
    me.cmpList.mode = "parked";
    me.cmpList.showPaydown = false;
    me.cmpList.Main = me;
    $.when(me.cmpList.buildArrayBons(true, false)).then(function(val) {
        if (val)

        // if (me.cmpList.buildArrayBons(true, false))
        {
            text = POS.lang.getText("MESSAGES", "DONT_FORGET_BONS_PARKED");
            POS.message.show(text, "info", "OK", me, me.beginTransferPOS);
        } else
            me.beginTransferPOS(me);

    });
}

legacy.beginTransferPOS = function(me) {
    POS.isPOSBeingClosed = true;

    var text = POS.lang.getText("MESSAGES", "TRANSFER_BALANCE_LOAD");
    POS.hourGlass.show(text, me, me.doTransferPOS);
}

legacy.doTransferPOS = function(me) {
    me.cmpBalance = me.addChild(CPosBalancing);
    me.cmpBalance.mode = "transfer";
    $.when(me.cmpBalance.draw()).done(function() {
        me.cmpBalance.inputs[0].field.input.setFocus();
        POS.hourGlass.hide();
    });
}

legacy.endPOS = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.userProps["POC"] == 0) {
        var text = POS.lang.getText("MESSAGES", "NO_RIGHT_CLOSE_POS");
        POS.message.show(text, "warning", "");
        return;
    }

    me.cmpList = me.addChild(CPosReceiptList);
    me.cmpList.mode = "parked";
    me.cmpList.showPaydown = false;
    me.cmpList.Main = me;
    $.when(me.cmpList.buildArrayBons(true, false)).then(function(val) {
        if (val)
        //if (me.cmpList.buildArrayBons(true, false))
        {
            text = POS.lang.getText("MESSAGES", "STILL_BONS_PARKED");
            POS.message.show(text, "question", "YESNO", me, me.doEndPOS);
        } else
            me.doEndPOS(me, "YES");
    });
}

legacy.doEndPOS = function(me, result) {

    if (result != "YES")
        return;
    $.when(Trans.isBKClosed()).then(function(val) {
        if (POS.EnvProperties.BANKOMATTERMINAL == "YES" && !val) {
            var text = POS.lang.getText("MESSAGES", "STILL_BK_NOT_CLOSED");
            POS.message.show(text, "question", "YESNO", me, me.doDoEndPOS);
        } else {
            me.doDoEndPOS(me, "YES");
        }
    });
}

legacy.doDoEndPOS = function(me, result) {

    if (result != "YES")
        return;

    POS.isPOSBeingClosed = true;

    var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX_LOAD");
    POS.hourGlass.show(text, me, me.finallyEndPOS);
}

legacy.finallyEndPOS = function(me) {
    me.cmpBalance = me.addChild(CPosBalancing);
    me.cmpBalance.mode = "end";
    $.when(me.cmpBalance.draw()).then(function() {
        me.cmpBalance.inputs[0].field.input.setFocus();
        POS.hourGlass.hide();
        setTimeout(POS.runPOSPush(), 600000); //10 min.  anch Abschluss -> POSpush
    });
}

legacy.addProd = function(component, parameters, me) {
    //var me = POS.getComponentById(parameters[0]);
    if (!me) {
        me = POS.getComponentById(parameters[0]);
    }
    //var me = POS.getCurrentSession();
    var prodId = parameters[1];
    var from = parameters[2];

    log.add(4, "Info", "Adding product " + prodId + " from " + from, "");

    // Der Zweig from == "scanner" gehoerte zum Funkscanner (entfallen
    // 26.8.2026) - der Handscanner am Kassenplatz liefert "screen".
    // Kein Artikel auf einen Bon, der gerade abgeschlossen wird (siehe CBarcodeReader).
    if (me.BonManager.isProcessingPayment) {
        log.add(2, "Warning", "Produkt " + prodId + " verworfen - Bon wird gerade abgeschlossen");
        return;
    }
    if (from == "screen")
        return me.BonManager.preAddEntryPOS(me.vacuumInfoValue(me), prodId);

}

legacy.makeBonEditButtons = function() {
    var xml = new CXmlHandler("etc/layout/LYT_bonEditButtons.xml");

    var btns = new Array();

    var xmlBtns = xml.rootElement.getElementsByTagName("button");

    for (var n = 0; n < xmlBtns.length; n++) {
        btns[n] = new CPushButton(this.id, "BonEditBtn");
        btns[n].properties = combine(btns[n].properties,
            xml.getObject(xmlBtns[n], false)["button"]);
        btns[n].properties.attributes.onMousedown_parameter_0 = this.id;
    }

    btns[0].properties.attributes.onMousedown_parameter_1 = -230;
    btns[0].properties.events.onMousedown = this.scroll;

    btns[7].properties.attributes.onMousedown_parameter_1 = 230;
    btns[7].properties.events.onMousedown = this.scroll;

    btns[1].properties.events.onMousedown = this.removePos;
    btns[2].properties.events.onMousedown = this.correctQty;
    btns[3].properties.events.onMousedown = this.correctPrc;
    btns[4].properties.events.onMousedown = this.grantRebAbsolute;
    btns[5].properties.events.onMousedown = this.grantRebPercent;
    btns[5].properties.events.onMousedown = this.applySuperRebate;
    btns[6].properties.events.onMousedown = this.manualEANCode;

    return btns;
}

legacy.applySuperRebate = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Adding super rebate", "");
    if (typeof POS.EnvProperties.SUPERREBATECODE != "undefined")
        var code = POS.EnvProperties.SUPERREBATECODE;
    else
        code = "6660000000208";

    var barcodeReader = new CBarcodeReader();
    barcodeReader.processInput(code, me, "screen");
}

legacy.customerAdmin = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Customer admin on", "");

    var text = POS.lang.getText("MESSAGES", "PLEASE_WAIT");
    //POS.hourGlass.show(text, me, me.doCustomerAdmin);
    text = "Diese Funktion ist leider nicht verfügbar!";
    POS.message.show(text, "warning", "");
}

legacy.doCustomerAdmin = function(me) {
    me.cmpCustomer = me.addChild(CCustomerAdmin);
    $.when(me.cmpCustomer.draw()).then(function() {
        me.cmpCustomer.components.inputField.field.input.setFocus();
        POS.hourGlass.hide();
    });
}

legacy.scroll = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.components.bonContainer.scroll(parameters[1]);
}

legacy.removePos = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Removing position in Bon", "");
    me.BonManager.removePos();
}

legacy.correctQty = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Changing product quantity in Bon", "");
    me.BonManager.correctQty(me.vacuumInfoValue(me));
}

legacy.correctPrc = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Changing product price in Bon", "");
    me.BonManager.correctPrc(me.vacuumInfoValue(me));
}

legacy.grantRebAbsolute = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Applying absolute rebate in Bon", "");
    me.BonManager.grantRebAbsolute(me.vacuumInfoValue(me), false, null, "Rabatt");
}

legacy.grantRebPercent = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    log.add(4, "Info", "Applying percent rebate in Bon", "");
    me.BonManager.grantRebPercent(me.vacuumInfoValue(me) / 100);
}

legacy.cancelBon = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.doCancelBon(me);
}

legacy.doCancelBon = function(me) {
    if (!me.BonManager.currentBonId) {
        var text = POS.lang.getText("MESSAGES", "NO_BON");

        // Der if-Zweig gehoerte zum Funkscanner (entfallen 26.8.2026); an der
        // Kassa lief immer der else-Zweig.
        POS.message.show(text, "warning", "");

        return;
    }

    log.add(4, "Info", "Cancel bon mode in", "");

    me.cancelCmp = me.addChild(CCancelBonMode);
    me.cancelCmp.Main = me;
    me.cancelCmp.draw();
}

legacy.payment = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.payParam = parameters[1];

    if (me.cmpOnlyPay && me.cmpOnlyPay.transmitted) {
        var text = POS.lang.getText("MESSAGES", "PLEASE_WAIT");
        POS.hourGlass.show(text, null, null);
        me.cmpOnlyPay.doCancel(me.cmpOnlyPay);
        me.isPaydown = true;
        POS.hourGlass.hide();
    }

    if (!me.BonManager.currentBonId) {
        var text = POS.lang.getText("MESSAGES", "NO_BON");
        POS.message.show(text, "warning", "");
        return;
    }

    me.payUserSum = parseFloat(me.vacuumInfoValue(me));
    me.payOpenSum = parseFloat(me.BonManager.getSumArray()[6]);
    if (me.payUserSum > me.payOpenSum)
        me.payUserSum = me.payOpenSum;
    $.when(me.BonManager.checkCustomerThresholdPrice()).then(function(thresholdBonParams) {
        //var thresholdBonParams = me.BonManager.checkCustomerThresholdPrice();
        if (thresholdBonParams.length > 0) {
            var moneyToGo = thresholdBonParams[0];
            var rebatePercent = thresholdBonParams[1];
            text = POS.lang.getText("MESSAGES", "THRESHOLD_BON_1").replace("%amount%", float2StringD2(moneyToGo));
            text += POS.lang.getText("MESSAGES", "THRESHOLD_BON_2").replace("%percent%", float2StringD2(rebatePercent));
            POS.message.show(text, "warning", "YESNO", me, me.continuePayment);
        } else
            me.continuePayment(me, "YES");
    });
}

legacy.continuePayment = function(me, result) {
    if (result != "YES")
        return;

    if ((me.payParam == 3 || me.payParam == 4) && me.payUserSum < me.payOpenSum && !me.isPaydown) {
        var text = POS.lang.getText("MESSAGES", "CONF_CARD_LESS_SUM").replace("%amount%",
            float2StringD2(me.payUserSum));
        POS.message.show(text, "warning", "YESNO", me, me.doPayment);
    } else
        me.doPayment(me, "YES");
}

legacy.doPayment = function(me, result) {
    if (result != "YES")
        return;

    // Hier stand bis 23.8.2026 ein `if (false)`-Zweig, der CZIPInput
    // (eine stillgelegte jQuery-Kundenumfrage) geoeffnet haette. Screen und
    // Zweig sind entfernt - der else-Fall war schon immer der einzige, der
    // lief.
    me.enterPayment(me);
}

legacy.enterPayment = function(me) {
    log.add(4, "Info", "Payment: " + me.payParam, "");
    me.BonManager.addEntryPAY(me.payParam, me.payUserSum);
}

legacy.globalRebate = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

		POS.message.show("Diese Funktion ist nicht mehr verfügbar!", "warning", "OK");
		return;
	
    if (!me.BonManager.currentBonId) {
        var text = POS.lang.getText("MESSAGES", "NO_BON");
        POS.message.show(text, "warning", "");
        return;
    }

    log.add(4, "Info", "Adding global rebate", "");

    me.BonManager.addEntryGRB(me.vacuumInfoValue(me) / 100);
}

legacy.finishbon = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.BonManager.showSum();
}

legacy.vacuumInfoValue = function(component) {
    var ret = remEuropeanComma(component.components.info.input.properties.attributes.value);
    component.components.info.input.properties.attributes.value = "";
    component.components.info.input.reAttribute();

    log.add(4, "Info", "Input field in MainPOS was cleared for user " + component.UID, "");

    return ret;
}

legacy.enterInfo = function(component) {
    component.BonManager.switchSumInfo(false);
}

legacy.cancelClosedBon = function(bonId) {
    log.add(4, "Info", "Cancel closed bon mode in", "");


    this.cancelClosedCmp = this.addChild(CCancelClosedBonMode);
    this.cancelClosedCmp.Main = this;
    this.cancelClosedCmp.bonId = bonId;
    this.cancelClosedCmp.draw();
}

export const CMainPOS = defineReactScreen({
  className: 'CMainPOS',
  // this.name der Legacy-Klasse - daraus baut der Builder die DOM-Klasse.
  // Ohne das steht dort "CComponent MainPOS" statt "CMainPOS MainPOS"
  // (bridge.js:80 faellt auf 'CComponent' zurueck). Die meisten Screens
  // brauchen es nicht, weil ihr Legacy-this.name wirklich CComponent war -
  // CMainPOS setzt es aber ausdruecklich.
  legacyName: 'CMainPOS',
  // Wurzel laut Fixture: class="CMainPOS MainPOS". Das "MainPOS" kommt aus
  // dem zweiten Konstruktor-Argument des Legacy (this.construct(id,
  // "MainPOS")) und ist im Bridge-Vertrag das addclass.
  addclass: 'MainPOS',
  htmlType: 'div',
  Screen: MainPOSScreen,
  // rootStyles bleibt LEER wie im Legacy-Konstruktor. Der z-Index der Wurzel
  // (style="z-index: 2") kommt zur Laufzeit vom Sitzungsverwalter:
  // addSession setzt ihn auf 2, majorHide/majorShow schalten zwischen 0 und 2
  // um. Die Bridge baut die Wurzel ueber denselben Builder wie CComponent,
  // properties.styles wirkt also unveraendert - das ist die Grundlage des
  // Mehrbenutzer-Stapels (siehe Runbook).
  rootStyles: {},
  // collector liest die Bridge von der INSTANZ (bridge.js:130), nicht aus der
  // Spezifikation - deshalb hier und nicht als eigener Schluessel.
  fields: () => ({
    collector: true,
    UID: null, cmpPOSDisabled: null, cmpPriceInput: null, cmpOnlyPay: null,
    cmpCopies: null, cmpPrepaidPayment: null, isPaydown: false,
    lastSubmenuId: 0, inputFocusEnabled: null,
  }),
  initialState: { prodStand: 0 },

  // prepare laeuft VOR dem Mount - hier gehoert alles hin, was React schon
  // beim ersten Rendern braucht (die sieben Knopf-Felder). Der Ablauf folgt
  // dem Legacy-prepare, nur ohne dessen ersten Teil: die Kinder aus der
  // Layout-XML zu bauen ist jetzt Sache der JSX.
  prepare(bridge) {
    const defret = jQuery.Deferred();
    const def = jQuery.Deferred();
    const def2 = jQuery.Deferred();

    $.when(POS.rights.getUserProps(bridge.UID)).then(function (userProps) {
      bridge.userProps = userProps;
      if (!bridge.userProps) { defret.resolve(); return; }

      bridge.leftPadLegacy = bridge.makeLeftPadButtons();
      bridge.leftPadKnoepfe = knoepfeAusLegacy(bridge.leftPadLegacy);

      // WICHTIG, live belegt am 24.8.2026: ohne das Recht "COL" laeuft der
      // then-Zweig NIE - im Legacy bleibt dann def unaufgeloest, und alles
      // dahinter (InfoIcons, BonManager) entsteht nicht. Ein Benutzer ohne
      // UR-Zeilen stirbt deshalb mitten im Sitzungsaufbau, und
      // CSessionHandler.addSession faellt ueber das fehlende InfoIcons
      // ("Cannot read properties of undefined (reading 'highlightIcon')").
      // Das Verhalten wird hier UNVERAENDERT nachgebaut - es zu "reparieren"
      // waere eine Verhaltensaenderung und gehoert nicht in einen Port.
      if (typeof bridge.userProps['COL'] != 'undefined' && bridge.userProps['COL'] == -1) {
        $.when(bridge.makeProdCatButtons()).then(function (ProdCatButtons) {
          bridge.reiterLegacy = ProdCatButtons;
          bridge.reiterKnoepfe = knoepfeAusLegacy(ProdCatButtons);

          const its = new Array(ProdCatButtons.length);
          bridge.inhaltLegacy = new Array(ProdCatButtons.length);
          const iterator = function it(i) {
            if (i < its.length) {
              $.when(bridge.makeProdButtons(i)).then(function (ProdButtons) {
                bridge.inhaltLegacy[i] = ProdButtons;
                its[i] = knoepfeAusLegacy(ProdButtons);
                iterator(i + 1);
              });
            } else {
              bridge.inhaltKnoepfe = its;
              def.resolve();
            }
          };
          iterator(0);

          $.when(def).done(function () {
            $.when(bridge.makePaymentButtons()).then(function (PaymentButtons) {
              bridge.paymentLegacy = PaymentButtons[0];
              bridge.payment2Legacy = PaymentButtons[1];
              bridge.bonfncLegacy = bridge.makeBonFncButtons();
              bridge.boneditLegacy = bridge.makeBonEditButtons();
              bridge.paymentKnoepfe = knoepfeAusLegacy(bridge.paymentLegacy);
              bridge.payment2Knoepfe = knoepfeAusLegacy(bridge.payment2Legacy);
              bridge.bonfncKnoepfe = knoepfeAusLegacy(bridge.bonfncLegacy);
              bridge.boneditKnoepfe = knoepfeAusLegacy(bridge.boneditLegacy);

              // CInfoIcons braucht nur POS.sessions.openSessions[UID], und das
              // setzt addSession VOR draw() - laeuft hier also schon.
              bridge.InfoIcons = new CInfoIcons(bridge.UID);
              bridge.infoIconLegacy = bridge.InfoIcons.makeInfoIcons();
              bridge.infoIconKnoepfe = knoepfeAusLegacy(bridge.infoIconLegacy);

              def2.resolve();
            });
          });
        });
      } else {
        // Ohne COL: statt der Produktauswahl das POSysseus-Logo.
        bridge.zeigeLogo = true;
        def2.resolve();
      }

      $.when(def2).done(function () {
        bridge.prepared = true;
        defret.resolve(bridge);
      });
    });

    return defret.promise();
  },

  // mountLegacy laeuft NACH dem Mount - hier gehoert alles hin, was Handles
  // voraussetzt. Die Namen zuerst: 14 der 18 werden von FREMDEM Code gelesen
  // (Namens-Inventur im Runbook), und ein fehlender Name faellt erst spaeter
  // und woanders auf - daran ist CPosBalancing gestolpert.
  mountLegacy(bridge) {
    bridge.nameComponent('0', 'topPad');
    bridge.nameComponent('1', 'leftPad');
    bridge.nameComponent('2', 'numPad');
    bridge.nameComponent('3', 'prodSelect');
    bridge.nameComponent('4', 'payment');
    bridge.nameComponent('5', 'payment2');
    bridge.nameComponent('6', 'bonedit');
    bridge.nameComponent('7', 'bonfnc');
    bridge.nameComponent('8', 'bonContainer');
    bridge.nameComponent('9', 'info');
    bridge.nameComponent('10', 'sumcaption');
    bridge.nameComponent('11', 'suminfo');
    bridge.nameComponent('12', 'statusBar');
    bridge.nameComponent('13', 'infoIcons');

    // Die zwei VERSCHACHTELTEN Namensraeume (Runbook): fremder Code greift
    // bonContainer.components.bon und statusBar.components.dateTimeInfo.
    // nameComponent kann das schon - es laeuft den Pfad ab und registriert
    // den Namen am ELTERNTEIL (bridge.js:295-303). nameComponent('8:0','bon')
    // ergibt also components.bonContainer.components.bon.
    // Zeilen- und Spaltenstile des Bon-Gitters kommen aus der Layout-XML
    // (LYT_mainPOS.xml, <bon><row>/<column>) - ohne sie fehlt jeder Bonzeile
    // ihre Hoehe, die Trennlinie und die Schriftgroesse.
    const gitter = gitterVorbereiten(bridge, '8:0', {
      canSelect: true,
      row: { styles: { height: '5%', borderBottomStyle: 'solid',
                       borderBottomWidth: '2px', fontSize: '1.3vw' } },
      column: { styles: { textAlign: 'left', verticalAlign: 'top' } },
    });
    preVorbereiten(bridge, '8:1');
    bridge.nameComponent('8:0', 'bon');
    bridge.nameComponent('8:1', 'bonSum');

    // Die beiden Summen-Label brauchen ihr CCaption-Kind als .caption -
    // darueber schreibt der Bonrenderer (switchSumInfo). Die Voreinstellung
    // "Text Label" des Legacy-CTextLabel wird dabei uebernommen, sie steht im
    // leeren Hauptbildschirm tatsaechlich da.
    for (const pfad of ['10', '11']) {
      const label = verdrahteTextLabel(bridge, pfad);
      if (!label.caption.properties.value) {
        label.caption.properties.value = 'Text Label';
        label.caption.klassenWert = 'Text Label';
      }
    }

    captionVorbereiten(bridge, '12:0');
    captionVorbereiten(bridge, '12:1');
    captionVorbereiten(bridge, '12:2');
    bridge.nameComponent('12:0', 'userInfo');
    bridge.nameComponent('12:1', 'versionInfo');
    bridge.nameComponent('12:2', 'dateTimeInfo');

    // Die Knopf-FELDER an die Handles. Fremder Code liest sie - die
    // Namens-Inventur im Runbook zaehlt sie auf, und das Uebersehen hat
    // sofort zugeschlagen: CUserRights.makeRights liest
    // components.payment.buttons.length (userrights.js:123) und starb an
    // undefined, noch bevor irgendein Pixel verglichen werden konnte.
    // Die Deskriptoren sind fuers RENDERN, diese Felder fuers VERHALTEN -
    // beides wird gebraucht.
    const felder = [
      ['leftPad', 'leftPadLegacy'], ['payment', 'paymentLegacy'],
      ['payment2', 'payment2Legacy'], ['bonedit', 'boneditLegacy'],
      ['bonfnc', 'bonfncLegacy'], ['infoIcons', 'infoIconLegacy'],
    ];
    for (const [name, quelle] of felder) {
      if (bridge[quelle]) bridge.components[name].buttons = bridge[quelle];
    }
    if (bridge.reiterLegacy) bridge.components.prodSelect.tabs = bridge.reiterLegacy;
    if (bridge.inhaltLegacy) bridge.components.prodSelect.items = bridge.inhaltLegacy;

    // Objektbeziehungen, die im Legacy der XML-Builder gesetzt hat.
    const eingabe = verdrahteInputField(bridge, '9');
    const numPad = bridge.handleAt('2');
    numPad.controlFields = numPad.controlFields || [];
    numPad.controlFields.push(eingabe);

    eingabe.properties.events.onBlur = bridge.restoreInputFocus;
    eingabe.properties.attributes.onBlur_parameter_0 = bridge.id;
    eingabe.properties.changeValueCallback.cls = bridge;
    eingabe.properties.changeValueCallback.fnc = bridge.enterInfo;

    verdrahteTabbedPushPanel(bridge, '3', (bridge.reiterKnoepfe || []).length);
    // Eigener Zaehler fuer die Produktauswahl - NICHT der gemeinsame
    // gitterStand, den das Bon-Gitter hochzaehlt. Sonst renderte die
    // Produktauswahl bei jeder Bon-Aenderung mit.
    reDrawUeberReact(bridge, '3', 'prodStand');

    if (bridge.InfoIcons) bridge.InfoIcons.hideAllInfoIcons();

    if (bridge.userProps) {
      bridge.BonManager = new CBonManager(
        bridge.components.bonContainer, bridge.components.payment,
        bridge.components.info, bridge.components.suminfo,
        bridge.components.sumcaption, bridge.userProps, bridge);
      bridge.EANManager = new CEANManager(bridge);

      POS.timeout.register('CDateTime' + bridge.UID, bridge.updateDateTime, bridge, 40);
      POS.timeout.register('CTestInput' + bridge.UID, bridge.testInput, bridge, 30);

      bridge.components.statusBar.components.dateTimeInfo.properties.value = bridge.getDateTime();
      bridge.components.statusBar.components.dateTimeInfo.prepare();
      bridge.components.statusBar.components.versionInfo.properties.value = 'POSweb ' + POS.ProgramVersion;
      bridge.components.statusBar.components.versionInfo.prepare();
      bridge.components.statusBar.components.userInfo.properties.value = bridge.userProps['Name'];
      bridge.components.statusBar.components.userInfo.prepare();
    }

    // EINMAL neu rendern, zum Schluss. Grund (teuer gefunden am 24.8.2026):
    // CMainPOS ist ein COLLECTOR - sein DOM entsteht losgeloest in einem
    // transmitDocument und wird erst DANACH ins Dokument gehaengt. Waehrend
    // mountLegacy laeuft, findet document.getElementById() die Elemente also
    // noch nicht, und jeder direkte DOM-Schreibvorgang (captionVorbereiten
    // .prepare/.reValue, preVorbereiten) verpufft LAUTLOS - er prueft brav
    // auf null und tut dann nichts.
    // Die Werte stehen aber im Handle, und genau daraus liest die JSX. Ein
    // Neu-Rendern erzeugt deshalb exakt das, was die Legacy-Aufrufe gemeint
    // haben. Spaetere Aufrufe zur Laufzeit (updateDateTime alle 40s) treffen
    // das Dokument dann normal.
    bridge.update({});
  },

  // --- Pflanzenerkennung (Knopf "Pflanze erkennen" in der Kopfleiste) ----
  // NEU am 21.9.2026, keine Legacy-Entsprechung. Der Knopf selbst sitzt am
  // Anmeldebildschirm (CLoginScreen - dort stehen auch "Handbuch" und
  // "Kalibrieren"), die Arbeit gehoert aber hierher: es geht um die
  // Produktauswahl DIESER Sitzung.
  methods: {
    // Bild aufnehmen lassen, Antwort abwarten, Reiter aufschalten.
    // Die Aufnahme und das Fragen des Modells macht der Kassenserver
    // (kassenserver/src/routes/pflanze.js) - die Kasse kennt weder Kamera
    // noch Modell noch Schluessel.
    pflanzeErkennen(bridge) {
      if (bridge.zeigeLogo) {
        // Benutzer ohne COL = -1 haben gar keine Produktauswahl (prepare
        // zeigt ihnen das Logo). Dann gibt es nichts aufzuschalten.
        POS.message.show('Dieser Benutzer hat keine Produktauswahl.', 'warning', 'OK');
        return;
      }
      // Auf einem Touchscreen wird jeder Knopf gern zweimal getroffen, und
      // die Kamera ist ein Geraet, kein Dienst. Der Kassenserver wehrt den
      // zweiten Aufruf zwar auch ab (409), aber dann steht schon eine
      // Fehlermeldung im Weg.
      if (bridge.erkennungLaeuft)
        return;
      bridge.erkennungLaeuft = true;
      kiVergessen();
      var altesBild = document.getElementById('kiVorschau');
      if (altesBild) altesBild.remove();
      // MIT Text: die Erkennung dauert je nach Modell mehrere Sekunden, und
      // eine stumme Sanduhr sagt nicht, worauf man wartet.
      POS.hourGlass.show('Pflanze wird erkannt ...');

      $.ajax({
        url: 'pflanze/erkennen',
        type: 'POST',
        dataType: 'json',
        cache: false,
        // Grosszuegig: ein Bildmodell auf eigener Maschine braucht laenger
        // als ein gehostetes. Das eigentliche Zeitlimit steht zentral in
        // der Konfig (zeitlimitMs), dies hier ist nur der Notnagel.
        timeout: 60000,
      }).done(function (antwort) {
        if (antwort && antwort.vorschau)
          bridge.vorschauZeigen(antwort.vorschau, antwort.ok
            ? (antwort.unsicher ? 'Unsicher: ' : 'Erkannt: ') + (antwort.buttonName || antwort.gruppenName || antwort.ware)
            : '', antwort.sicherheit);
        if (!antwort || !antwort.ok) {
          POS.message.show('Pflanzenerkennung: ' +
            ((antwort && antwort.fehler) || 'keine Antwort'), 'warning', 'OK');
          return;
        }
        if (antwort.unsicher) {
          // Zu unsicher: NICHT umschalten. Ein Bildschirm, der von selbst
          // auf die falsche Gruppe springt, kostet mehr Zeit als einer, der
          // stehen bleibt.
          POS.message.show('Nicht sicher erkannt' +
            (antwort.ware ? ' ("' + antwort.ware + '")' : '') +
            '.\nBitte Warengruppe selbst waehlen.', 'warning', 'OK');
          return;
        }
        $.when(bridge.oeffneWarengruppe(antwort.gruppe)).done(function (gefunden) {
          if (!gefunden) {
            POS.message.show('Erkannt: ' + (antwort.gruppenName || antwort.gruppe) +
              ' - diese Warengruppe gibt es an dieser Kasse nicht.', 'warning', 'OK');
            return;
          }
          // Mit Zentrale kommt auch der Button (Unterkategorie): ihn im
          // aufgeschalteten Reiter hervorheben - der Kassierer tippt selbst
          // (Nutzer-Entscheid 29.9.2026, kein automatisches Bonieren).
          var aufraeumen = antwort.prodId ? bridge.buttonMarkieren(antwort.prodId) : null;
          // Das erste danach bonierte Produkt geht als Label an die Zentrale.
          if (antwort.id) kiMerken(antwort.id, aufraeumen);
        });
      }).fail(function (xhr) {
        // Der Kassenserver schickt Fehler als JSON mit ok:false und Status
        // 4xx/5xx - jQuery landet dann hier, nicht in done().
        var grund = '';
        var fehlerDaten = {};
        try { fehlerDaten = JSON.parse(xhr.responseText) || {}; grund = fehlerDaten.fehler || ''; } catch (e) { grund = ''; }
        if (fehlerDaten.vorschau) bridge.vorschauZeigen(fehlerDaten.vorschau, '', null);
        POS.message.show('Pflanzenerkennung nicht moeglich.' +
          (grund ? '\n' + grund : ''), 'warning', 'OK');
      }).always(function () {
        bridge.erkennungLaeuft = false;
        POS.hourGlass.hide();
      });
    },

    // Das aufgenommene Bild kurz einblenden (Nutzer 29.9.2026): so sieht der
    // Kassierer, was die Kamera erfasst hat - Ausschnitt, Abstand, Licht.
    // Oben mittig ueber dem Bon-Bereich, 6 s oder bis zum Antippen; die
    // Produkttasten bleiben frei. Eine neue Erkennung ersetzt das alte Bild.
    vorschauZeigen(bridge, base64, text, sicherheit) {
      var alt = document.getElementById('kiVorschau');
      if (alt) alt.remove();
      var rahmen = document.createElement('div');
      rahmen.id = 'kiVorschau';
      var bild = document.createElement('img');
      bild.src = 'data:image/jpeg;base64,' + base64;
      rahmen.appendChild(bild);
      var zeile = document.createElement('div');
      zeile.className = 'kiVorschauText';
      zeile.textContent = text
        ? text + (typeof sicherheit === 'number' ? ' (' + Math.round(sicherheit * 100) + ' %)' : '')
        : 'Aufnahme der Kamera';
      rahmen.appendChild(zeile);
      var weg = function () { if (rahmen.parentNode) rahmen.remove(); };
      rahmen.addEventListener('mousedown', weg);
      setTimeout(weg, 6000);
      document.body.appendChild(rahmen);
    },

    // Im aufgeschalteten Reiter den Produktknopf mit PROD_ID <prodId>
    // hervorheben (CSS-Klasse "kiTreffer", lib/css/pos.css) und, falls er
    // auf einer anderen Seite des Reiters steht, dorthin blaettern.
    // Liefert die Funktion, die die Markierung wieder entfernt - sie laeuft
    // beim naechsten Bonieren, bei der naechsten Erkennung oder nach 30 s.
    buttonMarkieren(bridge, prodId) {
      var tab = bridge.components && bridge.components.prodSelect;
      var inhalt = tab && tab.currentPanel;
      if (!inhalt) return null;
      var seiten = inhalt.children || [];
      for (var p = 0; p < seiten.length; p++) {
        var knoepfe = (seiten[p] && seiten[p].children) || [];
        for (var k = 0; k < knoepfe.length; k++) {
          var attr = knoepfe[k] && knoepfe[k].properties && knoepfe[k].properties.attributes;
          if (!attr || Number(attr.onMousedown_parameter_1) !== Number(prodId)) continue;
          if (seiten.length > 1) bridge.reiterSeiteZeigen(inhalt, p);
          var el = document.getElementById(knoepfe[k].id);
          if (!el) return null;
          el.classList.add('kiTreffer');
          var weg = function () { el.classList.remove('kiTreffer'); };
          var uhr = setTimeout(weg, 30000);
          return function () { clearTimeout(uhr); weg(); };
        }
      }
      return null;
    },

    // BTN_ID einer Warengruppe -> den zugehoerigen Reiter aufschalten.
    // Liefert ein jQuery-Deferred auf true/false (Regel 4 des Runbooks:
    // $.when-Aufrufer bekommen nie native Promises).
    oeffneWarengruppe(bridge, btnId) {
      var def = jQuery.Deferred();

      // Die Reiter entstehen in makeProdCatButtons aus GENAU dieser
      // Auswahlmenge und Reihenfolge - dort nur ohne BTN_ID im SELECT,
      // weshalb die Zuordnung Nummer -> Reiterindex hier neu gefragt wird.
      // Eigener SQL-Text statt Aenderung am bestehenden: der alte bleibt
      // damit zeichengleich (Regel 3, Master/Slave-Routing).
      var sql = 'SELECT BTN_ID FROM BTN WHERE Parent_BTN_ID = 0 ORDER BY BTN_ID';
      $.when(POS.SQLiteCon.execute(sql)).then(function (rows) {
        var index = -1;
        for (var i = 0; i < rows.length; i++) {
          if (Number(rows[i].BTN_ID) === Number(btnId)) {
            index = i;
            break;
          }
        }
        if (index < 0) {
          def.resolve(false);
          return;
        }
        def.resolve(bridge.reiterWaehlen(index));
      });

      return def.promise();
    },

    // Reiter Nummer <index> aufschalten - dasselbe, was ein Fingertipp
    // darauf tut, inklusive Blaettern, wenn er auf der zweiten Seite steht
    // (bei 15 Warengruppen in einem 3x4-Raster sind das die letzten vier).
    reiterWaehlen(bridge, index) {
      var tab = bridge.components && bridge.components.prodSelect;
      if (!tab || !tab.tabsPanel)
        return false;

      var seiten = tab.tabsPanel.children || [];
      for (var p = 0; p < seiten.length; p++) {
        var knoepfe = (seiten[p] && seiten[p].children) || [];
        for (var k = 0; k < knoepfe.length; k++) {
          var knopf = knoepfe[k];
          var attr = knopf && knopf.properties && knopf.properties.attributes;
          // Der Reiter-Knopf traegt seinen Index als zweiten Parameter
          // (widgets.jsx, reiterMitSchalter). Die Blaetterknoepfe tragen
          // dort eine Komponenten-ID ("338:3:0:1") - Number() macht daraus
          // NaN, sie fallen also schon hier durch.
          if (!attr || Number(attr.onMousedown_parameter_1) !== index)
            continue;

          bridge.reiterSeiteZeigen(tab.tabsPanel, p);
          // WOERTLICH der Handler des Reiter-Knopfes; er erwartet den
          // angeklickten Knopf und [TabbedPanel-ID, Reiterindex].
          CTabbedPushPanel.prototype.tabSelect(knopf, [tab.id, index]);
          return true;
        }
      }
      return false;
    },

    // Seite <p> des Reiter-Rasters sichtbar machen - ueber die echten
    // Blaetter-Handler, damit nextPanel/prevPanel/currentPanel genauso
    // stehen wie nach einem Tipp auf ">" bzw. "<".
    reiterSeiteZeigen(bridge, paged, p) {
      var ziel = paged.children[p];
      if (!ziel || !paged.currentPanel || paged.currentPanel === ziel)
        return;

      var seiten = paged.panels || paged.children || [];
      // Notbremse: lieber stehen bleiben als eine Endlosschleife an der
      // Kassa - eine unverkettete Seite (nextPanel fehlt) bricht ohnehin ab.
      for (var schritt = 0; schritt < 20; schritt++) {
        var jetzt = paged.currentPanel;
        if (jetzt === ziel)
          return;
        var vorwaerts = seiten.indexOf(ziel) > seiten.indexOf(jetzt);
        if (vorwaerts && jetzt.nextPanel)
          CPagedPushPanel.prototype.nextPage(null, [paged.id, jetzt.id]);
        else if (!vorwaerts && jetzt.prevPanel)
          CPagedPushPanel.prototype.prevPage(null, [paged.id, jetzt.id]);
        else
          return;
      }
    },
  },

  legacyMethods: legacy,
});
